import { Router } from '@lightningjs/sdk'
import {
  NavigationPointers,
  PointerOrientation,
  VerticalPointerSize,
} from '../../components/NavigationPointers/NavigationPointers'
import TvCard from '../../components/TvCards/TvBaseCard'
import TvEditFavoritesLayout, {
  States as EditFavoritesStates,
} from '../../components/TvEditFavoritesLayout/TvEditFavoritesLayout'
import TvTab from '../../components/TvSearchLayout/TabMenu/TvTab'
import { getColor, grey960 } from '../../constants/colors'
import { LEFT_SAFE_ZONE_MARGIN } from '../../constants/general'
import { AppConfig } from '../../services/appConfig'
import { MessageModelTypesEnum } from '../../types/flo-enums'
import { captureScreenData } from '../../utils/segmentAnalytics'
import { genericError, networkError } from '../../widgets/modal'
import PageHandler from '../handlers/pageHandler'

export default class EditFavoritesPage extends PageHandler {
  private _siteId: string
  private _navId: string
  private _limit: string
  private _offset: string
  private _fromSettings = false
  private _url: string

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      collision: true,
      color: getColor(grey960),
      TvEditFavoritesLayout: {
        x: LEFT_SAFE_ZONE_MARGIN,
        type: TvEditFavoritesLayout,
      },
      NavigationPointers: {
        type: NavigationPointers,
        pointerOrientation: PointerOrientation.VERTICAL,
        verticalPointerSize: VerticalPointerSize.MINI,
      },
    }
  }

  static _states() {
    return [
      class NotReady extends this {
        _handleKey() {
          return true
        }

        $exit() {
          void captureScreenData(this.tvEditFavoritesLayout.data?.analytics)
        }
      },
      class Ready extends this {
        $enter() {
          this.checkNavigationPointersVisibility()
        }

        _getFocused() {
          return this.tvEditFavoritesLayout
        }
      },
    ]
  }

  get pageText() {
    return 'Edit Favorites'
  }

  get tvEditFavoritesLayout() {
    return this.tag('TvEditFavoritesLayout') as TvEditFavoritesLayout
  }

  get navigationPointers() {
    return this.tag('NavigationPointers') as NavigationPointers
  }

  get isSportPicker() {
    return this.tvEditFavoritesLayout?.tabMenu?.tabIndex === 0
  }

  _onUrlParams({ url }: { url: string }) {
    this._url = url
    const urlParams = new URLSearchParams(window.location.href.split('?')?.[1])
    this._siteId = urlParams.get('site_id')
    this._navId = urlParams.get('nav_id')
    this._limit = urlParams.get('limit') ?? '3'
    this._offset = urlParams.get('offset') ?? '0'
  }

  async _onMounted() {
    this._isRoot = false

    this._setState('NotReady')

    await this.getFavoritesData()
  }

  async getFavoritesData() {
    const data = await AppConfig.shared.experienceApi.getEditFavorites(this._url)

    if (data?.error) {
      const error = data.isNetworkError ? networkError : genericError
      this.setModal(true, error)
      return
    }

    this.tvEditFavoritesLayout.data = data

    if (!this.tvEditFavoritesLayout.lastSelectedTab) {
      this.tvEditFavoritesLayout.tabMenu.currentTab.toggleTab(false)
      this.tvEditFavoritesLayout.tabMenu.tabIndex = data.tab_menu.items.findIndex(
        (item) => item.is_selected === true,
      )
      this.tvEditFavoritesLayout.tabMenu.currentTab.setActive()
    }

    this._setState('Ready')
  }

  _handleLeft() {
    Router.focusWidget('Menu')
  }

  historyState(params) {
    if (params) {
      // When coming back from the sports picker, we must restore the tab the user had previously selected
      this.tvEditFavoritesLayout.restoreTabSelection(params.last_selected_tab_index)
    } else {
      return {
        last_selected_tab_index: this.tvEditFavoritesLayout.tabMenu?.tabIndex,
      }
    }
  }

  $upMovement() {
    this.tvEditFavoritesLayout?._setState(EditFavoritesStates.Grid)
    this.adjustPointerUpMovement()
    this.tvEditFavoritesLayout?.tvGridContainer?._handleUp()
    this.checkNavigationPointersVisibility()
  }

  adjustPointerUpMovement() {
    const isLastRowFocused = this.tvEditFavoritesLayout?.tvGridContainer?.isFocusingLastRow

    if (this.isSportPicker && isLastRowFocused) {
      this.tvEditFavoritesLayout.tvGridContainer.currentRow -= 1
    }
  }

  $downMovement() {
    this.tvEditFavoritesLayout?._setState(EditFavoritesStates.Grid)
    this.adjustPointerDownMovement()
    this.tvEditFavoritesLayout?.tvGridContainer?._handleDown()
    this.checkNavigationPointersVisibility()
  }

  adjustPointerDownMovement() {
    const isFirstRowFocused = this.tvEditFavoritesLayout?.tvGridContainer?.isFocusingFirstRow

    if (this.isSportPicker && isFirstRowFocused) {
      this.tvEditFavoritesLayout.tvGridContainer.currentRow += 1
    }
  }

  $nextPageLoadComplete() {
    this.checkNavigationPointersVisibility()
  }

  checkNavigationPointersVisibility() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    const totalRows = this.tvEditFavoritesLayout?.tvGridContainer?.totalEl

    const currentRow = this.tvEditFavoritesLayout?.tvGridContainer?.currentRow
    const isFirstRowFocused = this.tvEditFavoritesLayout?.tvGridContainer?.isFocusingFirstRow
    const isSecondRowFocused = currentRow === 1
    const isSecondToLastRowFocused = currentRow === totalRows - 2
    const isLastRowFocused = this.tvEditFavoritesLayout?.tvGridContainer?.isFocusingLastRow

    const showTopPointer = this.isSportPicker
      ? !(isFirstRowFocused || isSecondRowFocused)
      : !isFirstRowFocused
    const showBottomPointer = this.isSportPicker
      ? !(isSecondToLastRowFocused || isLastRowFocused)
      : !isLastRowFocused

    const currentChild = this.tvEditFavoritesLayout?.tvGridContainer?.currentChild
    const isMessage = currentChild?.data?.[0]?.type === MessageModelTypesEnum.Message
    const isError = this.tvEditFavoritesLayout?.error?.visible
    const isGridEmpty = totalRows === 0 || (totalRows === 1 && isMessage) || isError

    this.adjustNavigationPointersCollisionBox()

    this.tvEditFavoritesLayout.pointerBackButton.visible = isCursorVisible
    this.navigationPointers.visible = isCursorVisible
    this.navigationPointers.topContainer.y = this.tvEditFavoritesLayout?.gradientBottom?.finalY
    this.navigationPointers.showTopPointer = showTopPointer && !isGridEmpty && isCursorVisible
    this.navigationPointers.showBottomPointer = showBottomPointer && !isGridEmpty && isCursorVisible
    this.navigationPointers.bottomContainer.collision =
      showBottomPointer && !isGridEmpty && isCursorVisible

    this._refocus()
  }

  _handleScroll({ deltaY }) {
    const isGoingDown = deltaY > 0
    const isGoingUp = !isGoingDown
    const currentChild = this.tvEditFavoritesLayout?.tvGridContainer?.currentChild
    const totalRows = this.tvEditFavoritesLayout?.tvGridContainer?.totalEl
    const isMessage = currentChild?.data?.[0]?.type === MessageModelTypesEnum.Message
    const isError = this.tvEditFavoritesLayout?.error?.visible
    const isGridEmpty = totalRows === 0 || (totalRows === 1 && isMessage) || isError

    if (isGridEmpty) {
      return
    }

    if (isGoingDown) {
      this.adjustPointerDownMovement()
    } else {
      this.adjustPointerUpMovement()
    }

    this.tvEditFavoritesLayout?._setState(EditFavoritesStates.Grid)

    const currentRow = this.tvEditFavoritesLayout?.tvGridContainer?.currentRow
    const isSecondRowFocused = currentRow === 1
    const isSecondToLastRowFocused = currentRow === totalRows - 2

    if (
      !this.isSportPicker ||
      (isGoingDown && !isSecondToLastRowFocused) ||
      (isGoingUp && !isSecondRowFocused) ||
      totalRows === 3
    ) {
      this.tvEditFavoritesLayout?.tvGridContainer?.handleScroll({ deltaY })
    }

    this.checkNavigationPointersVisibility()
  }

  $updateFocusByHover(element) {
    if (element === this.tvEditFavoritesLayout?.addFavoritesButton) {
      this.tvEditFavoritesLayout?._setState(EditFavoritesStates.AddFavorites)
    } else if (element instanceof TvTab) {
      this.tvEditFavoritesLayout?._setState(EditFavoritesStates.Tabs)
    } else if (element instanceof TvCard) {
      this.tvEditFavoritesLayout?._setState(EditFavoritesStates.Grid)
      const { rowIndex, cardIndex } = element
      this.tvEditFavoritesLayout.tvGridContainer.currentRow = rowIndex
      this.tvEditFavoritesLayout.tvGridContainer.currentChild.currentTile = cardIndex
    }

    this._refocus()
  }

  // Increases the height of elements that overflow the navigation pointers that prevent
  // the user from clicking the tiles, even when not being fully covered by the gradients,
  // depending of the tab selected on the top menu
  adjustNavigationPointersCollisionBox() {
    const currentRow = this.tvEditFavoritesLayout?.tvGridContainer?.currentRow
    const totalElements = this.tvEditFavoritesLayout?.tvGridContainer?.totalEl
    const isFirstRowFocused = this.tvEditFavoritesLayout?.tvGridContainer?.isFocusingFirstRow
    const isSecondRowFocused = currentRow === 1
    const isSecondToLastRowFocused = currentRow === totalElements - 2
    const isLastRowFocused = this.tvEditFavoritesLayout?.tvGridContainer?.isFocusingLastRow
    const isFocusInTheMiddle =
      !isFirstRowFocused && !isSecondRowFocused && !isLastRowFocused && !isSecondToLastRowFocused

    if (this.isSportPicker && (isFirstRowFocused || isSecondRowFocused)) {
      this.navigationPointers.topOverflowCollisionBox.h = 0
      this.navigationPointers.bottomOverflowCollisionBox.h = 75
    } else if (this.isSportPicker && (isLastRowFocused || isSecondToLastRowFocused)) {
      this.navigationPointers.topOverflowCollisionBox.h = 75
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    } else if (this.isSportPicker && isFocusInTheMiddle) {
      this.navigationPointers.topOverflowCollisionBox.h = 150
      this.navigationPointers.bottomOverflowCollisionBox.h = 140
    } else if (isFirstRowFocused) {
      this.navigationPointers.topOverflowCollisionBox.h = 0
      this.navigationPointers.bottomOverflowCollisionBox.h = 75
    } else if (isLastRowFocused) {
      this.navigationPointers.topOverflowCollisionBox.h = 70
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    } else {
      // In the middle for entity cards
      this.navigationPointers.topOverflowCollisionBox.h = 40
      this.navigationPointers.bottomOverflowCollisionBox.h = 0
    }
  }

  _onCursorEnter() {
    this.checkNavigationPointersVisibility()
  }

  _onCursorLeave() {
    this.checkNavigationPointersVisibility()

    if (this.tvEditFavoritesLayout?.state === EditFavoritesStates.Grid) {
      this.tvEditFavoritesLayout?.tvGridContainer?.currentChild?.currentChild?._focus()
    } else if (this.tvEditFavoritesLayout?.state === EditFavoritesStates.Tabs) {
      this.tvEditFavoritesLayout?.tabMenu?.currentTab?._focus()
    } else if (this.tvEditFavoritesLayout?.state === EditFavoritesStates.AddFavorites) {
      this.tvEditFavoritesLayout?.addFavoritesButton?._focus()
    }
  }
}
